import type { GestureDetectorConfig, RollDetectorConfig, ThrowDetectorConfig } from './types.js';

export const CC_NAMES: Record<number, string> = {
  0: 'MoveSmoothed',
  1: 'SpinSmoothed',
  2: 'FreeFall',
  3: 'OrientX',
  4: 'OrientY',
  5: 'OrientZ',
  6: 'Energy',
};

export const MIDI = {
  NOTE_ON: 0x90,
  NOTE_OFF: 0x80,
  CC: 0xb0,
  CHANNEL_MASK: 0x0f,
  COMMAND_MASK: 0xf0,
} as const;

export const DEFAULT_GESTURE_CONFIG: GestureDetectorConfig = {
  freeFallThreshold: 15,
  idleTimeoutMs: 3000,
  sustainedWindowMs: 500,
  sustainedMinEvents: 3,
  abruptStopMinMotionMs: 300,
  rollingUpdateIntervalMs: 150,
  airborneUpdateIntervalMs: 100,
  spinningThreshold: 10,
  directionChangeSmoothingWindow: 5,
  // Slow path (medium rolls)
  rollSlowWindowMs: 150,
  rollSlowSmoothingTauMs: 180,
  rollSlowStartThreshold: 2.5,
  rollSlowStartHoldMs: 120,
  // Fast path (fast rolls)
  rollFastWindowMs: 50,
  rollFastSmoothingTauMs: 80,
  rollFastStartThreshold: 34.6,
  rollFastStartHoldMs: 60,
  rollFastMinOmega: 17.1,
  // Spin in energy
  rollSpinSmoothingTauMs: 100,
  rollFastSpinWeight: 4,
  rollSlowSpinWeight: 3,
  // Spin-assisted immediate start
  rollSpinHighConfidence: 6.7,
  // End condition
  rollEndEnergyThreshold: 0.5,
  rollEndMinOmega: 1.0,
  rollEndMinSpin: 2.6,
  rollEndHoldMs: 350,
  rollEndMaxLowFreqUpdates: 5,
  // Impact grace
  rollImpactGraceMs: 200,
  rollImpactOmegaThreshold: 30,
  // Shared
  rollMinStartAngle: 1.0,
  rollBackoffMs: 200,
  rollMinGravityMagnitude: 25,
  rollMaxGravityMagnitude: 90,
  rollGravityBufferSize: 30,
  rollMinOscillationFreq: 0.3,
};

export const DEFAULT_ROLL_CONFIG: RollDetectorConfig = {
  spinningThreshold: DEFAULT_GESTURE_CONFIG.spinningThreshold,
  directionChangeSmoothingWindow: DEFAULT_GESTURE_CONFIG.directionChangeSmoothingWindow,
  rollingUpdateIntervalMs: DEFAULT_GESTURE_CONFIG.rollingUpdateIntervalMs,
  rollSlowWindowMs: DEFAULT_GESTURE_CONFIG.rollSlowWindowMs,
  rollSlowSmoothingTauMs: DEFAULT_GESTURE_CONFIG.rollSlowSmoothingTauMs,
  rollSlowStartThreshold: DEFAULT_GESTURE_CONFIG.rollSlowStartThreshold,
  rollSlowStartHoldMs: DEFAULT_GESTURE_CONFIG.rollSlowStartHoldMs,
  rollFastWindowMs: DEFAULT_GESTURE_CONFIG.rollFastWindowMs,
  rollFastSmoothingTauMs: DEFAULT_GESTURE_CONFIG.rollFastSmoothingTauMs,
  rollFastStartThreshold: DEFAULT_GESTURE_CONFIG.rollFastStartThreshold,
  rollFastStartHoldMs: DEFAULT_GESTURE_CONFIG.rollFastStartHoldMs,
  rollFastMinOmega: DEFAULT_GESTURE_CONFIG.rollFastMinOmega,
  rollSpinSmoothingTauMs: DEFAULT_GESTURE_CONFIG.rollSpinSmoothingTauMs,
  rollFastSpinWeight: DEFAULT_GESTURE_CONFIG.rollFastSpinWeight,
  rollSlowSpinWeight: DEFAULT_GESTURE_CONFIG.rollSlowSpinWeight,
  rollSpinHighConfidence: DEFAULT_GESTURE_CONFIG.rollSpinHighConfidence,
  rollEndEnergyThreshold: DEFAULT_GESTURE_CONFIG.rollEndEnergyThreshold,
  rollEndMinOmega: DEFAULT_GESTURE_CONFIG.rollEndMinOmega,
  rollEndMinSpin: DEFAULT_GESTURE_CONFIG.rollEndMinSpin,
  rollEndHoldMs: DEFAULT_GESTURE_CONFIG.rollEndHoldMs,
  rollEndMaxLowFreqUpdates: DEFAULT_GESTURE_CONFIG.rollEndMaxLowFreqUpdates,
  rollImpactGraceMs: DEFAULT_GESTURE_CONFIG.rollImpactGraceMs,
  rollImpactOmegaThreshold: DEFAULT_GESTURE_CONFIG.rollImpactOmegaThreshold,
  rollMinStartAngle: DEFAULT_GESTURE_CONFIG.rollMinStartAngle,
  rollBackoffMs: DEFAULT_GESTURE_CONFIG.rollBackoffMs,
  rollMinGravityMagnitude: DEFAULT_GESTURE_CONFIG.rollMinGravityMagnitude,
  rollMaxGravityMagnitude: DEFAULT_GESTURE_CONFIG.rollMaxGravityMagnitude,
  rollGravityBufferSize: DEFAULT_GESTURE_CONFIG.rollGravityBufferSize,
  rollMinOscillationFreq: DEFAULT_GESTURE_CONFIG.rollMinOscillationFreq,
};

export const DEFAULT_THROW_CONFIG: ThrowDetectorConfig = {
  freeFallThreshold: DEFAULT_GESTURE_CONFIG.freeFallThreshold,
  spinningThreshold: DEFAULT_GESTURE_CONFIG.spinningThreshold,
  airborneUpdateIntervalMs: DEFAULT_GESTURE_CONFIG.airborneUpdateIntervalMs,
};
